import React from "react";
import "./App.css";

import { Routes, Route } from "react-router-dom";

// Authentication Pages
import LoginScreen from "./screens/authentication/LoginScreen/LoginScreen";

import EmailResetRequestScreen from "./screens/authentication/EmailResetRequestScreen/EmailResetRequestScreen";
import PasswordResetScreen from "./screens/authentication/PasswordResetScreen/PasswordResetScreen";

import EmailRegistrationScreen from "./screens/authentication/EmailRegistrationScreen/EmailRegistrationScreen";
import CompleteRegistrationScreen from "./screens/authentication/CompleteRegistrationScreen/CompleteRegistrationScreen";

// Home and Settings pages
import HomeScreen from "./screens/clinicalDashboard/generalScreens/HomeScreen/HomeScreen";
import SettingsScreen from "./screens/clinicalDashboard/generalScreens/SettingsScreen/SettingsScreen";

// Profile Pages
import ProfileScreen from "./screens/clinicalDashboard/profileScreens/ProfileScreen/ProfileScreen";
import ProfileTagsScreen from "./screens/clinicalDashboard/profileScreens/ProfileTagsScreen/ProfileTagsScreen";

// Patient Pages
import PatientDatabaseScreen from "./screens/clinicalDashboard/patientScreens/PatientDatabaseScreen/PatientDatabaseScreen";
import PatientCreationScreen from "./screens/clinicalDashboard/patientScreens/PatientCreationScreen/PatientCreationScreen";
import PatientDetailsScreen from "./screens/clinicalDashboard/patientScreens/PatientDetailsScreen/PatientDetailsScreen";

// All Entries Page
import AllEntriesMenuScreen from "./screens/clinicalDashboard/entriesScreens/AllEntries/AllEntriesMenuScreen";

// Entry Menu Screens
import PhotoMenuScreen from "./screens/clinicalDashboard/entriesScreens/menuScreens/regular/PhotoMenuScreen/PhotoMenuScreen";
import ComparisonMenuScreen from "./screens/clinicalDashboard/entriesScreens/menuScreens/regular/ComparisonMenuScreen/ComparisonMenuScreen";

import PsoriasisMenuScreen from "./screens/clinicalDashboard/entriesScreens/menuScreens/psoriasis/PsoriasisMenuScreen/PsoriasisMenuScreen";
import PsoriasisSegmentationMenuScreen from "./screens/clinicalDashboard/entriesScreens/menuScreens/psoriasis/PsoriasisSegmentationMenuScreen/PsoriasisSegmentationMenuScreen";
import SegmentationMenuScreen from "./screens/clinicalDashboard/entriesScreens/menuScreens/psoriasis/SegmentationMenuScreen/SegmentationMenuScreen";

// Entry Details Screens
import PhotoEntryScreen from "./screens/clinicalDashboard/entriesScreens/entryScreens/regular/PhotoEntryScreen/PhotoEntryScreen";
import ComparisonEntryScreen from "./screens/clinicalDashboard/entriesScreens/entryScreens/regular/ComparisonEntryScreen/ComparisonEntryScreen";

import PsoriasisEntryScreen from "./screens/clinicalDashboard/entriesScreens/entryScreens/psoriasis/PsoriasisEntryScreen/PsoriasisEntryScreen";
import PsoriasisSegmentationEntryScreen from "./screens/clinicalDashboard/entriesScreens/entryScreens/psoriasis/PsoriasisSegmentationEntryScreen/PsoriasisSegmentationEntryScreen";
import SegmentationEntryScreen from "./screens/clinicalDashboard/entriesScreens/entryScreens/psoriasis/SegmentationEntryScreen/SegmentationEntryScreen";

// Rapid Entry Wrapper
import RapidEntryScreen from "./screens/clinicalDashboard/entriesScreens/rapidScreens/RapidEntryScreen/RapidEntryScreen";

// 404 Error Page
import ErrorScreen from "./screens/errorScreens/ErrorScreen/ErrorScreen";

// Server configuration screens
import ServerConfigurationScreen from "./screens/configuration/ServerConfigurationScreen/ServerConfigurationScreen";
import ResetStateScreen from "./screens/configuration/ResetStateScreen/ResetStateScreen";

// Statistics
import PasiStatistics from "./screens/clinicalDashboard/statisticsScreens/psoriasisStatistics/PasiStatistics/PasiStatistics";

// Help Screens
import LegalAndContactScreen from "./screens/clinicalDashboard/helpScreens/LegalAndContactScreen/LegalAndContactScreen";
import DisclaimerScreen from "./screens/clinicalDashboard/helpScreens/DisclaimerScreen/DisclaimerScreen";

// Layouts
import GeneralLayout from "./components/layouts/GeneralLayout/GeneralLayout";
import AuthenticationLayout from "./components/layouts/AuthenticationLayout/AuthenticationLayout";
import ClinicalDashboardLayout from "./components/layouts/ClinicalDashboardLayout/ClinicalDashboardLayout";

// Helpers
import { useScrollToTop } from "./hooks/useScrollToTop";
import { useResetLoaderProcesses } from "./hooks/useResetLoaderProcesses";
import { usePartialApplicationReset } from "./hooks/usePartialApplicationReset";
import { useInactivityLogout } from "./hooks/useInactivityLogout";
import { useMobileMenuScrollLock } from "./hooks/useMobileMenuScrollLock";
import Backdrop from "./components/Backdrop/Backdrop";
// import ScreenSizeIndicator from "./helpers/ScreenSizeIndicator";

function App() {
  // Scroll to top of page on page change
  useScrollToTop();
  // Reset loader count after page refresh
  useResetLoaderProcesses();

  // Perform partial application reset after page refresh
  usePartialApplicationReset();

  // Prevent body scrolling when menu is open on mobile
  useMobileMenuScrollLock();

  // Auto logout user after a period of inactivity
  const TIMEOUT_DURATION = 1 * 1 * 15 * 60 * 1000; // d * h * min * sec * ms
  useInactivityLogout(TIMEOUT_DURATION);

  return (
    <>
      <Backdrop />
      {/* <ScreenSizeIndicator /> */}
      <Routes>
        <Route path="/" element={<GeneralLayout />}>
          {/* Public Routes */}

          {/* Authentication Routes */}
          <Route element={<AuthenticationLayout />}>
            <Route
              path="server_configuration"
              element={<ServerConfigurationScreen />}
            />
            <Route path="reset_state" element={<ResetStateScreen />} />
            <Route path="login" element={<LoginScreen />} />
            <Route
              path="email_registration"
              element={<EmailRegistrationScreen />}
            />
            <Route
              path="complete_registration/:user_id/:token/:user_email"
              element={<CompleteRegistrationScreen />}
            />
            <Route
              path="email_reset_request"
              element={<EmailResetRequestScreen />}
            />
            <Route
              path="password_reset/:user_id/:token/:user_email/:username"
              element={<PasswordResetScreen />}
            />
          </Route>
          {/* Private Routes */}
          <Route element={<ClinicalDashboardLayout />}>
            {/* Home and Settings pages */}
            <Route path="" element={<HomeScreen />} />
            <Route path="settings" element={<SettingsScreen />} />
            {/* Help pages */}
            <Route
              path="legal_and_contact"
              element={<LegalAndContactScreen />}
            />
            <Route path="disclaimer" element={<DisclaimerScreen />} />
            {/* Profile Pages */}
            <Route path="profile" element={<ProfileScreen />} />
            <Route path="profile_tags" element={<ProfileTagsScreen />} />
            {/* Patient Pages */}
            <Route
              path="patients/details/:id"
              element={<PatientDetailsScreen />}
            />
            <Route
              path="patients/database"
              element={<PatientDatabaseScreen />}
            />
            <Route path="patients/create" element={<PatientCreationScreen />} />
            {/* All Entries */}
            <Route
              path="entries/all_entries_menu/:id"
              element={<AllEntriesMenuScreen />}
            />
            {/* Entry Menu Screens // Regular Entries */}
            <Route
              path="entries/photo_menu/:id"
              element={<PhotoMenuScreen />}
            />
            <Route
              path="entries/comparison_menu/:id"
              element={<ComparisonMenuScreen />}
            />
            {/* Entry Menu Screens // Neural Network Entries */}
            <Route
              path="entries/psoriasis_menu/:id"
              element={<PsoriasisMenuScreen />}
            />
            <Route
              path="entries/segmentation_menu/:id"
              element={<SegmentationMenuScreen />}
            />
            <Route
              path="entries/psoriasis_segmentation_menu/:id"
              element={<PsoriasisSegmentationMenuScreen />}
            />
            {/* Entry Details Screens // Regular Entries */}
            <Route
              path="entries/photo_entry/:id"
              element={<PhotoEntryScreen />}
            />
            <Route
              path="entries/comparison_entry/:id"
              element={<ComparisonEntryScreen />}
            />
            {/* Entry Details Screens // Neural Network Entries */}
            <Route
              path="entries/psoriasis_entry/:id"
              element={<PsoriasisEntryScreen />}
            />
            <Route
              path="entries/segmentation_entry/:id"
              element={<SegmentationEntryScreen />}
            />
            <Route
              path="entries/psoriasis_segmentation_entry/:id"
              element={<PsoriasisSegmentationEntryScreen />}
            />
            {/* PASI Statistics */}
            <Route
              path="statistics/pasi_statistics/:id"
              element={<PasiStatistics />}
            />
            {/* Rapid */}
            <Route
              path="entries/rapid/comparison/:id"
              element={
                <RapidEntryScreen
                  EntryScreenComponent={ComparisonEntryScreen}
                />
              }
            />
            <Route
              path="entries/rapid/psoriasis_calculation/:id"
              element={
                <RapidEntryScreen EntryScreenComponent={PsoriasisEntryScreen} />
              }
            />
            <Route
              path="entries/rapid/regular_segmentation/:id"
              element={
                <RapidEntryScreen
                  EntryScreenComponent={SegmentationEntryScreen}
                />
              }
            />
            <Route
              path="entries/rapid/psoriasis_segmentation/:id"
              element={
                <RapidEntryScreen
                  EntryScreenComponent={PsoriasisSegmentationEntryScreen}
                />
              }
            />
          </Route>
          {/* Catch all */}
          <Route path="*" element={<ErrorScreen />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
